<script setup>
import BrandButton from "@/Components/Brand/BrandButton.vue";

const bullets = [
    "A Wish List that's easy to share",
    "Discover ideas and trends from experts",
    "Collaborate with friends and family",
    "Manage your list from anywhere",
];
</script>

<template>
    <section class="relative z-10 px-5">
        <div class="relative mx-auto max-w-[1472px]">
            <!--
                Continuous white panel: starts flush under the header band and
                extends ~84% down the red box so header + hero read as one white
                surface, with the red box overhanging the bottom.
            -->
            <div
                aria-hidden="true"
                class="absolute inset-x-0 top-0 bottom-[92px] rounded-b-brand bg-white"
            ></div>

            <!--
                Red hero box, inset from the white panel edges. Mobile sizes to
                its content (Figma 1-663 is 627px at 393 wide); the 574px floor
                belongs to the side-by-side desktop layout only.
            -->
            <div
                class="relative mx-3 flex flex-col overflow-hidden rounded-brand bg-brand-red shadow-brand-hero md:mx-9 md:min-h-[574px] md:flex-row md:items-center"
            >
                <!--
                    Mobile: warm radial glow behind the collage (Figma 1-664),
                    sitting off-centre top-left and fading into the red by the
                    headline. The desktop art has the glow baked in.
                -->
                <div
                    aria-hidden="true"
                    class="pointer-events-none absolute inset-x-0 top-0 h-[498px] bg-[radial-gradient(360px_420px_at_33%_18%,#ffb545_5%,#fc8c42bf_29%,#f9643f80_53%,#f2143900_100%)] md:hidden"
                ></div>
                <!--
                    Single flat export of the hero artwork (red panel, warm glow,
                    "GIFT HERO" watermark, and product collage), cropped tight to
                    the red box so it fills the container exactly. The live copy
                    below overlays the left side. Hidden on small screens where the
                    solid red background carries the section.
                -->
                <img
                    fetchpriority="high"
                    :src="asset('images/brand/hero-art.webp')"
                    alt=""
                    aria-hidden="true"
                    class="pointer-events-none absolute inset-0 block h-full w-full object-cover max-md:hidden"
                />

                <!--
                    Mobile: the wide flat art doesn't fit, so stack a dedicated
                    square collage above the copy, nearly edge to edge like the
                    mock (Figma 1-665: 332px in a 345px box). Hidden from md up.
                -->
                <img
                    fetchpriority="high"
                    :src="asset('images/brand/hero-art-mobile.webp')"
                    alt=""
                    aria-hidden="true"
                    class="pointer-events-none relative mx-auto w-full px-1.5 md:hidden"
                />

                <!--
                    Copy. Mobile follows Figma 1-701: 37px side padding, 46px
                    headline on two lines tucked up into the collage's empty
                    bottom edge, 14px bullets with gold dots, 44px Sign Up
                    button. From md the desktop treatment takes over.
                -->
                <div
                    class="relative -mt-6 w-full px-[37px] pb-10 md:mt-0 md:px-12 md:py-12 lg:px-16"
                >
                    <div class="max-w-[553px] text-white">
                        <h1
                            class="font-brand-display text-[46px] font-black italic uppercase leading-[0.87] tracking-[-0.02em] md:text-[clamp(3.5rem,7vw,82px)]"
                        >
                            Wish Lists<br />Made Simple
                        </h1>

                        <ul
                            class="mt-3 font-brand text-[14px] leading-[1.85] md:mt-7 md:space-y-3 md:text-[17px] md:leading-normal"
                        >
                            <li
                                v-for="bullet in bullets"
                                :key="bullet"
                                class="flex items-start gap-[11px] md:gap-3"
                            >
                                <span
                                    class="mt-[11px] size-1 shrink-0 rounded-full bg-[#ffb545] md:mt-2.5 md:size-[5px] md:bg-white"
                                ></span>
                                <span>{{ bullet }}</span>
                            </li>
                        </ul>

                        <BrandButton
                            :href="route('register')"
                            variant="navyOnRed"
                            class="mt-6 h-11 px-10 text-[15px] md:mt-9 md:h-auto md:px-8 md:py-3.5"
                        >
                            <span class="md:hidden">Sign Up</span>
                            <span class="max-md:hidden">Get Started</span>
                        </BrandButton>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
